import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PanelHeader from "components/panelheader/panelHeader";
import { Card, CardBody, Row, Col } from "reactstrap";
import { getEquiposSelect } from "services/equiposService";
import { getJugadoresSelect, saveCamisetaJugador } from "services/jugadoresService";
import { getSedesSelect } from "services/sedesService";
import {
  crearFecha,
  crearPartido,
  eliminarFecha,
  eliminarPartido,
  getTorneo,
  guardarFecha,
  guardarListaFeDeEquipo,
  guardarListaFeDeFecha,
  guardarPartido,
  guardarPartidosDeFecha,
  sacarEquipoDeZona,
  sacarEquipoDelTorneo,
  saveTorneo,
} from "services/torneosService";
import { toastBigSuccess, toastBigError, toastConfirm } from "components/utils/toasts";
import {
  cabeceraAlServidor,
  cambiosDelPartido,
  isObjectId,
  mapToServerModel,
  mapToViewModel,
  partidoAlServidor,
} from "./torneoMapper";
import { numeroDeFecha } from "./fechas";
import HomeScreen from "./HomeScreen";
import TeamsScreen from "./TeamsScreen";
import TeamScreen from "./TeamScreen";
import RoundScreen from "./RoundScreen";
import ZoneScreen from "./ZoneScreen";
import DateScreen from "./DateScreen";
import MatchScreen from "./MatchScreen";
import ImportFixtureScreen from "./ImportFixtureScreen";
import ImportEstructuraScreen from "./ImportEstructuraScreen";
import { figuraEnRueda, nuevoEquipoDeTorneo } from "./equipos";
import { contar, enumerar, fechasDeZonas, jugados, partidosDeFechas } from "./bajas";
import { genId, nuevoObjectId } from "./ids";
import { usePageLoading } from "hooks/usePageLoading";

export default function TorneosForm(props) {
  const doneLoading = usePageLoading(props);
  const { id } = useParams();
  const navigate = useNavigate();

  const isEditing = !!id && !id.includes("nuev");
  const [isMounted, setIsMounted] = useState(false);
  const [loading, setLoading] = useState(isEditing);
  const [equipos, setEquipos] = useState([]);
  const [jugadores, setJugadores] = useState([]);
  const [sedes, setSedes] = useState([]);

  const [torneo, setTorneo] = useState({
    nombre: "",
    ano: new Date().getFullYear(),
    equipos: [],
    ruedas: [],
  });

  // Un torneo ya guardado (tiene _id) muestra las opciones completas y auto-persiste
  // cada cambio. Uno nuevo solo muestra nombre/año + botón "Guardar Torneo".
  const isSaved = !!torneo._id;
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved | error

  // Pila de navegación (drill-down). Cada frame lleva el path completo.
  // levels: home | teams | team | round | zone | date | match | import | estructura
  const [nav, setNav] = useState([{ level: "home" }]);
  const current = nav[nav.length - 1];

  const fetchData = async () => {
    try {
      const [eq, ju, se] = await Promise.all([getEquiposSelect(), getJugadoresSelect(), getSedesSelect()]);
      if (Array.isArray(eq)) setEquipos(eq);
      if (Array.isArray(ju)) setJugadores(ju);
      if (Array.isArray(se)) setSedes(se);

      // Modo edición: cargar el torneo existente y mapearlo al modelo del front.
      if (isEditing) {
        const record = await getTorneo(id);
        if (record && record._id) setTorneo(mapToViewModel(record));
      }
    } catch (error) {
      console.error("Error cargando datos del torneo:", error);
    } finally {
      setLoading(false);
      doneLoading();
    }
  };

  useEffect(() => {
    if (!isMounted) {
      fetchData();
      setIsMounted(true);
    }
  }, [isMounted]);

  // Bloqueo de salida de la pantalla actual (hoy: fecha sin día o sin sede).
  // Es una función que avisa qué falta, o null si se puede volver. Se guarda en un
  // ref porque el listener de popstate se registra una sola vez.
  const bloqueoDeSalidaRef = useRef(null);
  // Los "atrás" que dispara la propia app (ej. al descartar una fecha vacía) ya
  // dejaron el nav en su lugar: el popstate que llega después no tiene que desapilar.
  const popProgramadoRef = useRef(false);

  // El botón atrás del navegador/celular hace pop de una pantalla.
  useEffect(() => {
    const onPop = () => {
      if (popProgramadoRef.current) {
        popProgramadoRef.current = false;
        return;
      }
      const bloqueo = bloqueoDeSalidaRef.current;
      if (bloqueo) {
        // El browser ya desapiló: reponemos la entrada para no perder profundidad.
        window.history.pushState({ depth: window.history.state && window.history.state.depth }, "");
        bloqueo();
        return;
      }
      setNav((s) => (s.length > 1 ? s.slice(0, -1) : s));
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // ---- Guardado -------------------------------------------------------------
  //
  // Desde acá se persisten dos cosas distintas, y por dos caminos distintos:
  //
  //  - La ESTRUCTURA (nombre, año, qué equipos juegan, ruedas y zonas) sigue yendo
  //    con el PUT del torneo entero. Se arma una vez, al principio y desde un solo
  //    lugar, así que mandarla completa no molesta a nadie.
  //
  //  - Todo lo que se carga DURANTE una fecha (el día y la sede, la lista de fe, los
  //    partidos y sus resultados) va de a una operación con los endpoints granulares,
  //    que escriben un solo subdocumento. Es lo que permite que dos personas carguen
  //    la misma fecha desde dos celulares sin pisarse: con el PUT ganaba el último en
  //    guardar, que mandaba el torneo entero tal como lo tenía en memoria — y con él,
  //    su copia vieja de todo lo que habían cargado los demás.
  //
  // Las dos cosas comparten el mismo guardado con debounce y van en ese orden: una
  // fecha nueva vive dentro de una zona, así que la zona tiene que estar en la base
  // antes de que salga el alta de la fecha.
  const ESPERA_GUARDADO = 700;

  // El torneo tal como está ahora, para leerlo desde los handlers que no
  // re-renderizan (el aviso al cerrar, el guardado en vuelo).
  const torneoRef = useRef(torneo);
  torneoRef.current = torneo;

  // La estructura cambió y todavía no salió el PUT.
  const estructuraSuciaRef = useRef(false);
  // Cola de operaciones granulares pendientes: clave -> { datos, enviar }. Es un Map
  // porque el orden importa (el alta de una fecha tiene que llegar antes que su lista
  // de fe) y porque editar dos veces lo mismo no tiene que salir dos veces: la
  // segunda se fusiona con la primera y conserva su lugar en la fila.
  const colaRef = useRef(new Map());
  const [colaVersion, setColaVersion] = useState(0);
  const guardandoRef = useRef(false);
  // La clave de la operación que está saliendo en este momento. Sigue en la cola
  // hasta que el servidor conteste, así que hay que poder distinguirla de una que
  // todavía no salió.
  const enVueloRef = useRef(null);
  // Hay algo que todavía no llegó al servidor: el debounce corriendo, un guardado en
  // vuelo o uno que falló. Lo lee el aviso al cerrar la pantalla.
  const hayPendientes = () => estructuraSuciaRef.current || colaRef.current.size > 0;
  const sinGuardarRef = useRef(false);
  // Las acciones masivas (importar partidos o estructura) piden que el próximo
  // guardado no espere el debounce: son muchos datos de una sola vez y 700 ms es una
  // ventana grande para perderlos si la página se recarga o se cierra justo ahí.
  const guardarYaRef = useRef(false);

  // Un cambio que persiste el PUT del torneo entero.
  const cambioDeEstructura = (actualizar) => {
    estructuraSuciaRef.current = true;
    setTorneo(actualizar);
  };

  // Deja una operación granular en la cola. `datos` es el cuerpo que se manda: si ya
  // había una operación con la misma clave los dos cuerpos se fusionan, porque dos
  // ediciones seguidas de lo mismo (la hora de un partido y después su cancha) tienen
  // que salir juntas y no sólo la última.
  const encolar = (clave, servicio, datos) => {
    const anterior = colaRef.current.get(clave);
    const cuerpo = { ...(anterior ? anterior.datos : null), ...datos };
    colaRef.current.set(clave, { datos: cuerpo, enviar: (torneoId) => servicio(torneoId, cuerpo) });
    setColaVersion((v) => v + 1);
  };

  // Lo que colgaba de algo que se borra deja de tener sentido: si se va la fecha, no
  // hay que mandar ni su lista de fe ni sus partidos. Devuelve true si entre lo que se
  // descartó estaba el alta de eso mismo: entonces nunca llegó a la base y tampoco hay
  // que mandar la baja.
  const descartarDeLaCola = (prefijo, claveDelAlta) => {
    const nuncaLlego = colaRef.current.has(claveDelAlta) && enVueloRef.current !== claveDelAlta;
    Array.from(colaRef.current.keys()).forEach((clave) => {
      if (clave.startsWith(prefijo)) colaRef.current.delete(clave);
    });
    return nuncaLlego;
  };

  const guardar = async () => {
    if (guardandoRef.current) return; // ya hay una tanda en vuelo; ésta la toma el finally
    const torneoId = torneoRef.current._id;
    if (!torneoId) return;

    guardandoRef.current = true;
    setSaveState("saving");
    let fallo = false;
    try {
      if (estructuraSuciaRef.current) {
        const aGuardar = torneoRef.current;
        estructuraSuciaRef.current = false;
        try {
          await saveTorneo(mapToServerModel(aGuardar));
        } catch (ex) {
          estructuraSuciaRef.current = true; // sigue sin guardarse
          throw ex;
        }
      }

      // En serie y en el orden en que se cargaron. Lo que se encole mientras corre la
      // tanda va en la vuelta siguiente.
      for (const [clave, operacion] of Array.from(colaRef.current.entries())) {
        enVueloRef.current = clave;
        await operacion.enviar(torneoId);
        // Si mientras salía se volvió a editar lo mismo, lo que quedó en la cola es
        // más nuevo que lo que se acaba de grabar: se deja para la vuelta siguiente.
        if (colaRef.current.get(clave) === operacion) colaRef.current.delete(clave);
      }
      setSaveState("saved");
    } catch (ex) {
      setSaveState("error");
      const estado = ex.response && ex.response.status;
      if (estado === 404 && enVueloRef.current) {
        // Eso ya no existe (lo borraron desde otro dispositivo). Reintentarlo no lo va
        // a arreglar y dejarlo en la cola haría fallar todas las tandas siguientes; el
        // resto de la cola sí se sigue mandando.
        colaRef.current.delete(enVueloRef.current);
        toastBigError("No se pudo guardar", "Algo que estabas editando ya no existe en el torneo. Recargá la pantalla.");
      } else {
        fallo = true;
        toastBigError("No se pudo guardar", "Revisá la conexión e intentá de nuevo.");
      }
    } finally {
      guardandoRef.current = false;
      enVueloRef.current = null;
      sinGuardarRef.current = hayPendientes();
      // Si algo falló no se reintenta solo (quedaría reintentando cada 700 ms sin
      // conexión): lo levanta el próximo cambio, y hasta entonces el aviso al cerrar
      // la pantalla lo mantiene visible.
      if (!fallo && hayPendientes()) setColaVersion((v) => v + 1);
    }
  };

  useEffect(() => {
    if (!isSaved || !hayPendientes()) return undefined;
    sinGuardarRef.current = true;

    // Las acciones masivas salen ya, sin pasar por un timer. Importar partidos cambia
    // el torneo y navega en el mismo gesto, y un setTimeout(0) programado justo ahí
    // puede quedar sin ejecutarse: el guardado no sale nunca y los partidos se pierden
    // al recargar, aunque en pantalla se vean cargados.
    if (guardarYaRef.current) {
      guardarYaRef.current = false;
      guardar();
      return undefined;
    }

    const timer = setTimeout(guardar, ESPERA_GUARDADO);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [torneo, colaVersion]);

  // Ocultar el indicador "Guardado" al rato.
  useEffect(() => {
    if (saveState !== "saved") return undefined;
    const t = setTimeout(() => setSaveState("idle"), 1600);
    return () => clearTimeout(t);
  }, [saveState]);

  // Aviso al cerrar/refrescar mientras haya algo sin persistir: un torneo nuevo que
  // todavía no se creó, o un cambio que el guardado automático no llegó a mandar
  // (el debounce corriendo, o un guardado que falló). Sin esto el cambio se pierde en
  // silencio y en pantalla parecía cargado.
  useEffect(() => {
    const onBeforeUnload = (e) => {
      const nuevoSinCrear = !torneoRef.current._id && !!torneoRef.current.nombre;
      if (!nuevoSinCrear && !sinGuardarRef.current) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  const push = (frame) => {
    setNav((s) => [...s, frame]);
    window.history.pushState({ depth: nav.length + 1 }, "");
  };
  const goBack = () => window.history.back();

  // Actualiza el frame actual (para persistir UI de la pantalla al drillear y volver).
  const updateCurrentFrame = (patch) => setNav((s) => [...s.slice(0, -1), { ...s[s.length - 1], ...patch }]);

  // ---- Selectores derivados (torneo = única fuente de verdad) ----
  const round = torneo.ruedas.find((r) => r.id === current.roundId);
  const zone = round && round.zones.find((z) => z.id === current.zoneId);
  const date = zone && zone.matchDates.find((d) => d.id === current.dateId);
  const match = date && date.matches.find((m) => m.id === current.matchId);

  // Guarda: si un frame apunta a una entidad ya eliminada, volvemos al padre.
  useEffect(() => {
    const orphan =
      (current.roundId && !round) ||
      (current.zoneId && !zone) ||
      (current.dateId && !date) ||
      (current.matchId && !match);
    if (orphan) setNav((s) => (s.length > 1 ? s.slice(0, -1) : s));
  }, [current, round, zone, date, match]);

  // ---- Setters de estructura (los persiste el PUT) ----
  const onInfoChange = (info) => cambioDeEstructura((p) => ({ ...p, nombre: info.nombre, ano: info.ano }));
  const onEquiposChange = (nuevos) => cambioDeEstructura((p) => ({ ...p, equipos: nuevos }));
  const onRuedasChange = (nuevas) => cambioDeEstructura((p) => ({ ...p, ruedas: nuevas }));

  const renameTeam = (updated) =>
    cambioDeEstructura((p) => ({ ...p, equipos: p.equipos.map((t) => (t.id === updated.id ? updated : t)) }));

  const updateRound = (updated) =>
    cambioDeEstructura((p) => ({ ...p, ruedas: p.ruedas.map((r) => (r.id === updated.id ? updated : r)) }));

  const updateZone = (roundId, updated) =>
    cambioDeEstructura((p) => ({
      ...p,
      ruedas: p.ruedas.map((r) =>
        r.id === roundId ? { ...r, zones: r.zones.map((z) => (z.id === updated.id ? updated : z)) } : r
      ),
    }));

  // ---- Cambios que se guardan de a uno ----
  //
  // Todo lo que cuelga de una fecha. Cada uno cambia el torneo en memoria SIN marcar
  // la estructura como sucia (no lo escribe el PUT) y deja su operación en la cola.
  // El backend nombra las cosas por los _id de Mongo: los de rueda, zona, fecha y
  // partido los genera el cliente al crearlas (ids.js), así que una fecha recién
  // creada ya se puede guardar sin esperar ninguna respuesta.

  const claveDeFecha = (fechaId, sufijo) => "fecha:" + fechaId + ":" + sufijo;
  const claveDePartido = (fechaId, partidoId, sufijo) =>
    "fecha:" + fechaId + ":partido:" + partidoId + ":" + sufijo;

  // Los _id de la fecha en la que estamos parados, tal como los espera el backend.
  const ubicacionDeFecha = (fechaId) => ({
    ruedaId: round._id,
    zonaId: zone._id,
    fechaId: fechaId,
  });

  // Cambia una zona en memoria (y a través de ella sus fechas).
  const enLaZona = (roundId, zoneId, cambiar) =>
    setTorneo((p) => ({
      ...p,
      ruedas: p.ruedas.map((r) =>
        r.id !== roundId ? r : { ...r, zones: r.zones.map((z) => (z.id !== zoneId ? z : cambiar(z))) }
      ),
    }));

  const enLaFecha = (dateId, cambiar) =>
    enLaZona(current.roundId, current.zoneId, (z) => ({
      ...z,
      matchDates: z.matchDates.map((d) => (d.id !== dateId ? d : cambiar(d))),
    }));

  const addDate = () => {
    // Se entra directo a la fecha nueva: fecha y sede son obligatorias y se cargan ahí.
    const nueva = { id: genId("date"), _id: nuevoObjectId(), date: "", sede: "", attendance: {}, matches: [] };
    enLaZona(current.roundId, current.zoneId, (z) => ({ ...z, matchDates: [...z.matchDates, nueva] }));
    encolar(claveDeFecha(nueva._id, "alta"), crearFecha, ubicacionDeFecha(nueva._id));
    push({ ...current, level: "date", dateId: nueva.id });
  };

  const removeDate = (dateId) => {
    const fecha = zone.matchDates.find((d) => d.id === dateId);
    enLaZona(current.roundId, current.zoneId, (z) => ({
      ...z,
      matchDates: z.matchDates.filter((d) => d.id !== dateId),
    }));
    if (!fecha || !fecha._id) return;
    const nuncaLlego = descartarDeLaCola("fecha:" + fecha._id, claveDeFecha(fecha._id, "alta"));
    if (nuncaLlego) return;
    encolar(claveDeFecha(fecha._id, "baja"), eliminarFecha, ubicacionDeFecha(fecha._id));
  };

  // El día y la sede de la fecha.
  const updateCabecera = (campos) => {
    const actualizada = { ...date, ...campos };
    enLaFecha(date.id, () => actualizada);
    encolar(claveDeFecha(date._id, "cabecera"), guardarFecha, {
      ...ubicacionDeFecha(date._id),
      ...cabeceraAlServidor(actualizada),
    });
  };

  // La lista de fe de un equipo en la fecha. Va equipo por equipo, que es la unidad
  // en la que se carga: dos personas anotando dos equipos de la misma fecha al mismo
  // tiempo no se pisan.
  const updateAsistencia = (teamId, jugadoresDelEquipo) => {
    enLaFecha(date.id, (d) => ({ ...d, attendance: { ...d.attendance, [teamId]: jugadoresDelEquipo } }));
    encolar(claveDeFecha(date._id, "lista-fe:" + teamId), guardarListaFeDeFecha, {
      ...ubicacionDeFecha(date._id),
      equipoId: teamId,
      jugadores: jugadoresDelEquipo,
    });
  };

  const addMatch = () => {
    // Sin equipos preseleccionados: los elige el usuario en la pantalla del partido.
    const nuevo = {
      id: genId("match"),
      _id: nuevoObjectId(),
      homeTeamId: "",
      awayTeamId: "",
      hora: "",
      cancha: "",
      wo: "",
      homeGoals: [],
      awayGoals: [],
    };
    enLaFecha(date.id, (d) => ({ ...d, matches: [...d.matches, nuevo] }));
    encolar(claveDePartido(date._id, nuevo._id, "alta"), crearPartido, {
      ...ubicacionDeFecha(date._id),
      partidoId: nuevo._id,
    });
    push({ ...current, level: "match", matchId: nuevo.id }); // abrir la pantalla del partido
  };

  const removeMatch = (partido) => {
    enLaFecha(date.id, (d) => ({ ...d, matches: d.matches.filter((m) => m.id !== partido.id) }));
    if (!partido._id) return;
    const nuncaLlego = descartarDeLaCola(
      "fecha:" + date._id + ":partido:" + partido._id,
      claveDePartido(date._id, partido._id, "alta")
    );
    if (nuncaLlego) return;
    encolar(claveDePartido(date._id, partido._id, "baja"), eliminarPartido, {
      ...ubicacionDeFecha(date._id),
      partidoId: partido._id,
    });
  };

  // Equipos, hora y cancha del partido. Se manda sólo lo que cambió: corregir la hora
  // no tiene por qué pisar el resultado que otro esté cargando de ese mismo partido.
  const updateMatch = (updated) => {
    const cambios = cambiosDelPartido(match, updated);
    enLaFecha(current.dateId, (d) => ({
      ...d,
      matches: d.matches.map((m) => (m.id === updated.id ? updated : m)),
    }));
    if (!updated._id || Object.keys(cambios).length === 0) return;
    encolar(claveDePartido(date._id, updated._id, "datos"), guardarPartido, {
      ...ubicacionDeFecha(date._id),
      partidoId: updated._id,
      ...cambios,
    });
  };

  // Los partidos del archivo reemplazan a los que hubiera en la fecha, así que lo que
  // quedara encolado de esos partidos ya no va.
  const importarPartidos = (matches) => {
    guardarYaRef.current = true; // acción masiva: no esperar el debounce
    descartarDeLaCola("fecha:" + date._id + ":partido:", null);
    enLaFecha(date.id, (d) => ({ ...d, matches }));
    encolar(claveDeFecha(date._id, "partidos"), guardarPartidosDeFecha, {
      ...ubicacionDeFecha(date._id),
      partidos: matches.map(partidoAlServidor),
    });
    goBack();
    toastBigSuccess("Partidos importados", matches.length + " partidos cargados en la fecha.");
  };

  // La lista de fe del equipo EN EL TORNEO (el roster del que después salen las listas
  // de fe de cada fecha). Los rosters se cargan en paralelo, uno por equipo, así que
  // tampoco pueden ir en el PUT del torneo entero.
  const refDeJugador = (pl) => pl._id || pl.value || pl.id;
  const updateRoster = (team, players) => {
    setTorneo((p) => ({ ...p, equipos: p.equipos.map((t) => (t.id === team.id ? { ...t, players } : t)) }));
    // El torneo guarda sólo la referencia al jugador. Si esa lista no cambió no hay
    // nada que mandar: es lo que pasa al escribir una camiseta, que se guarda en el
    // padrón y no acá. Un renglón todavía sin jugador elegido tampoco cuenta.
    const jugadores = players.map(refDeJugador).filter(isObjectId);
    const antes = team.players.map(refDeJugador).filter(isObjectId);
    if (jugadores.join(",") === antes.join(",")) return;
    encolar("equipo:" + team.id + ":lista-fe", guardarListaFeDeEquipo, { equipoId: team.id, jugadores });
  };

  // Al sacar un equipo de la zona se limpian su lista de fe y los partidos que lo
  // incluyan, con el resultado que tuvieran cargado. La poda de verdad la hace el
  // servidor sobre lo que hay en la base (acá sólo se refleja en pantalla): así no se
  // manda una copia vieja de las fechas y no se pisa lo que estén cargando los demás.
  const removeEquipoDeZona = (teamId) => {
    const enElEquipo = (m) => m.homeTeamId === teamId || m.awayTeamId === teamId;
    enLaZona(current.roundId, current.zoneId, (z) => ({
      ...z,
      teamIds: z.teamIds.filter((x) => x !== teamId),
      matchDates: z.matchDates.map((d) => ({
        ...d,
        attendance: Object.fromEntries(
          Object.entries(d.attendance || {}).filter(([equipoId]) => equipoId !== teamId)
        ),
        matches: (d.matches || []).filter((m) => !enElEquipo(m)),
      })),
    }));
    encolar("zona:" + zone._id + ":equipo:" + teamId, sacarEquipoDeZona, {
      ruedaId: round._id,
      zonaId: zone._id,
      equipoId: teamId,
    });
  };

  // Baja de un equipo del torneo: abandona la competencia. Se lo saca de todo lo que
  // colgaba de él en la ÚLTIMA rueda —las zonas que lo tenían, la lista de fe de cada
  // fecha y los partidos que jugaba, con el resultado que tuvieran cargado—.
  //
  // Las ruedas anteriores se consideran cerradas y no se tocan: sus partidos y su
  // tabla quedan como terminaron. Si el equipo jugó alguna de ellas no sale de la
  // lista del torneo sino que queda de `baja`, porque esas tablas sacan su nombre de
  // ahí. Se confirma antes, avisando qué se pierde.
  const removeEquipo = (teamId) => {
    const equipo = torneo.equipos.find((t) => t.id === teamId);
    if (!equipo) return;

    const ultima = torneo.ruedas.length ? torneo.ruedas[torneo.ruedas.length - 1] : null;
    const jugoCerradas = torneo.ruedas.slice(0, -1).some((r) => figuraEnRueda(r, teamId));

    const enElEquipo = (m) => m.homeTeamId === teamId || m.awayTeamId === teamId;
    const zonas = ultima ? ultima.zones.filter((z) => z.teamIds.includes(teamId)).length : 0;
    const partidos = ultima ? partidosDeFechas(fechasDeZonas(ultima.zones)).filter(enElEquipo) : [];

    const detalle = enumerar([
      zonas ? contar(zonas, "zona", "zonas") : "",
      partidos.length ? contar(partidos.length, "partido", "partidos") : "",
      jugados(partidos) ? contar(jugados(partidos), "resultado ya cargado", "resultados ya cargados") : "",
    ]);
    const texto =
      (detalle
        ? "En " + ultima.name + " sale de " + detalle + ", y de la lista de fe de las fechas."
        : "Todavía no está en ninguna zona ni partido de la última rueda.") +
      (jugoCerradas ? " Los resultados y tablas de las ruedas anteriores se mantienen." : "");

    toastConfirm("¿ Eliminar " + equipo.name + " del torneo ?", texto, () => {
      // Igual que al sacarlo de una zona: en pantalla se refleja acá, pero el que poda
      // las fechas en la base es el servidor.
      setTorneo((p) => ({
        ...p,
        equipos: jugoCerradas
          ? p.equipos.map((t) => (t.id === teamId ? { ...t, baja: true } : t))
          : p.equipos.filter((t) => t.id !== teamId),
        ruedas: p.ruedas.map((r, index) =>
          index !== p.ruedas.length - 1
            ? r
            : {
                ...r,
                zones: r.zones.map((z) => ({
                  ...z,
                  teamIds: z.teamIds.filter((x) => x !== teamId),
                  matchDates: z.matchDates.map((d) => ({
                    ...d,
                    attendance: Object.fromEntries(
                      Object.entries(d.attendance || {}).filter(([equipoId]) => equipoId !== teamId)
                    ),
                    matches: (d.matches || []).filter((m) => !enElEquipo(m)),
                  })),
                })),
              }
        ),
      }));
      encolar("equipo:" + teamId + ":baja", sacarEquipoDelTorneo, { equipoId: teamId });
      // Vuelve a estar disponible en el select de alta de equipos.
      setEquipos((p) =>
        p.some((e) => e.value === equipo._id)
          ? p
          : [...p, { label: equipo.name, value: equipo._id }].sort((a, b) => a.label.localeCompare(b.label))
      );
    });
  };

  // La camiseta es del jugador, no del torneo: se guarda en el padrón y se refleja
  // en el select y en todas las listas de fe donde esté ese jugador. En el torneo no
  // hay nada que guardar, así que el cambio en memoria no dispara ningún guardado.
  const updateCamiseta = async (jugadorId, camiseta) => {
    const jugador = jugadores.find((j) => j.value === jugadorId);
    if (jugador && (jugador.camiseta || "") === camiseta) return;
    try {
      await saveCamisetaJugador(jugadorId, camiseta);
      setJugadores((p) => p.map((j) => (j.value === jugadorId ? { ...j, camiseta } : j)));
      setTorneo((p) => ({
        ...p,
        equipos: p.equipos.map((t) => ({
          ...t,
          players: t.players.map((pl) => (pl.value === jugadorId ? { ...pl, camiseta } : pl)),
        })),
      }));
    } catch (ex) {
      toastBigError("No se pudo guardar la camiseta", "Intentá de nuevo en un momento.");
    }
  };

  // ---- Importación de la estructura básica ----
  // El Excel del año trae una solapa por zona. La estructura entra como una rueda nueva
  // a continuación de las que ya están (no se pisa nada de lo cargado) y los equipos que
  // todavía no estén en el torneo se suman a la lista general con su lista de fe.
  const nombreDeRuedaNueva = "Rueda " + (torneo.ruedas.length + 1);

  const aplicarEstructura = (zonas) => {
    const idsUsados = [...new Set(zonas.flatMap((z) => z.teamIds))];
    const faltantes = idsUsados.filter((id) => !torneo.equipos.some((t) => t.id === id));
    const nuevos = faltantes
      .map((id) => equipos.find((e) => e.value === id))
      .filter(Boolean)
      .map((option) => nuevoEquipoDeTorneo(option, jugadores));

    const rueda = {
      id: genId("round"),
      _id: nuevoObjectId(),
      name: nombreDeRuedaNueva,
      zones: zonas.map((z) => ({
        id: genId("zone"),
        _id: nuevoObjectId(),
        name: z.name,
        teamIds: z.teamIds,
        matchDates: [],
      })),
    };

    guardarYaRef.current = true; // acción masiva: no esperar el debounce
    // Un equipo que estaba de baja y vuelve a aparecer en la rueda nueva se reactiva.
    cambioDeEstructura((p) => ({
      ...p,
      equipos: [
        ...p.equipos.map((t) => (t.baja && idsUsados.includes(t.id) ? { ...t, baja: false } : t)),
        ...nuevos,
      ],
      ruedas: [...p.ruedas, rueda],
    }));
    // Los equipos que pasaron al torneo salen del select de alta, igual que en TeamsScreen.
    setEquipos(equipos.filter((e) => !faltantes.includes(e.value)));
    goBack();
    toastBigSuccess(
      "Estructura importada",
      rueda.zones.length + " zonas y " + idsUsados.length + " equipos cargados en " + rueda.name + "."
    );
  };

  // Crea el torneo (alta). A partir de acá aparecen equipos/ruedas y el resto
  // se auto-guarda; este botón ya no se muestra.
  const handleCreate = async () => {
    if (!torneo.nombre || !torneo.nombre.trim()) {
      toastBigError("Falta el nombre", "Ingresá un nombre para el torneo.");
      return;
    }
    try {
      const resp = await saveTorneo(mapToServerModel(torneo));
      const saved = resp && resp.data && resp.data.record;
      if (saved && saved._id) {
        estructuraSuciaRef.current = false; // el POST ya persistió; no re-guardar
        setTorneo((p) => ({ ...p, _id: saved._id }));
        navigate("/admin/torneos/" + saved._id, { replace: true, state: { action: "E" } });
        toastBigSuccess("Torneo creado", "Ahora podés cargar equipos, ruedas, zonas, fechas y partidos.");
      }
    } catch (ex) {
      toastBigError("No se pudo crear", "Revisá los datos e intentá de nuevo.");
    }
  };

  // ---- Fecha y sede obligatorias ----
  // Una fecha de zona no puede quedar sin día ni sede, así que no se sale de su
  // pantalla hasta completar ambos. Si la fecha está recién creada y todavía vacía,
  // en vez de trabar al usuario se le ofrece descartarla.
  const fechaIncompleta = current.level === "date" && !!date && (!date.date || !date.sede);
  const fechaVacia =
    !!date &&
    !date.date &&
    !date.sede &&
    (date.matches || []).length === 0 &&
    Object.values(date.attendance || {}).every((jugadoresDelEquipo) => !jugadoresDelEquipo || jugadoresDelEquipo.length === 0);

  const descartarFecha = () => {
    // El nav lo desapila la guarda de huérfanos al desaparecer la fecha; el
    // history.back() consume la entrada que repusimos al bloquear.
    popProgramadoRef.current = true;
    removeDate(current.dateId);
    window.history.back();
  };

  const avisarFechaIncompleta = () => {
    if (fechaVacia) {
      toastConfirm("¿Descartar esta fecha?", "Todavía no tiene día ni sede.", descartarFecha);
      return;
    }
    const faltantes = [!date.date && "la fecha", !date.sede && "la sede"].filter(Boolean).join(" y ");
    toastBigError("Faltan datos", "Completá " + faltantes + " antes de volver.");
  };

  useEffect(() => {
    bloqueoDeSalidaRef.current = fechaIncompleta ? avisarFechaIncompleta : null;
  });

  const takenByOtherZones =
    round && zone ? round.zones.filter((z) => z.id !== zone.id).flatMap((z) => z.teamIds) : [];

  const renderScreen = () => {
    switch (current.level) {
      case "teams":
        return (
          <TeamsScreen
            teams={torneo.equipos}
            equipos={equipos}
            jugadores={jugadores}
            onChange={onEquiposChange}
            onRemoveTeam={removeEquipo}
            onOpenTeam={(teamId) => push({ level: "team", teamId })}
            onBack={goBack}
          />
        );
      case "team": {
        const team = torneo.equipos.find((t) => t.id === current.teamId);
        if (!team) return null;
        return (
          <TeamScreen
            team={team}
            jugadores={jugadores}
            onRename={renameTeam}
            onRosterChange={(players) => updateRoster(team, players)}
            onCamisetaChange={updateCamiseta}
            onBack={goBack}
          />
        );
      }
      case "round":
        if (!round) return null;
        return (
          <RoundScreen
            round={round}
            torneoNombre={torneo.nombre}
            torneoAno={torneo.ano}
            teams={torneo.equipos}
            sedes={sedes}
            onChange={updateRound}
            onOpenZone={(zoneId) => push({ ...current, level: "zone", zoneId })}
            onBack={goBack}
          />
        );
      case "zone":
        if (!zone) return null;
        return (
          <ZoneScreen
            zone={zone}
            teams={torneo.equipos}
            takenByOtherZones={takenByOtherZones}
            onChange={(updated) => updateZone(current.roundId, updated)}
            onRemoveTeam={removeEquipoDeZona}
            onAddDate={addDate}
            onRemoveDate={removeDate}
            onOpenDate={(dateId) => push({ ...current, level: "date", dateId })}
            onBack={goBack}
          />
        );
      case "date":
        if (!zone || !date) return null;
        return (
          <DateScreen
            date={date}
            numero={numeroDeFecha(zone.matchDates, date.id)}
            zoneTeamIds={zone.teamIds}
            teams={torneo.equipos}
            sedes={sedes}
            section={current.section || "matches"}
            onSectionChange={(s) => updateCurrentFrame({ section: s })}
            onCabeceraChange={updateCabecera}
            onAsistenciaChange={updateAsistencia}
            onAddMatch={addMatch}
            onRemoveMatch={removeMatch}
            onOpenMatch={(matchId) => push({ ...current, level: "match", matchId })}
            onOpenImport={(filas) => push({ ...current, level: "import", filas })}
            onBack={goBack}
          />
        );
      case "import":
        if (!zone || !date) return null;
        return (
          <ImportFixtureScreen
            filas={current.filas || []}
            date={date}
            numero={numeroDeFecha(zone.matchDates, date.id)}
            zoneTeamIds={zone.teamIds}
            teams={torneo.equipos}
            onConfirm={importarPartidos}
            onBack={goBack}
          />
        );
      case "estructura":
        return (
          <ImportEstructuraScreen
            zonas={current.zonas || []}
            ruedaNombre={nombreDeRuedaNueva}
            equipos={equipos}
            teams={torneo.equipos}
            onConfirm={aplicarEstructura}
            onBack={goBack}
          />
        );
      case "match":
        if (!zone || !date || !match) return null;
        return (
          <MatchScreen
            match={match}
            zoneTeamIds={zone.teamIds}
            teams={torneo.equipos}
            onChange={updateMatch}
            onBack={goBack}
          />
        );
      case "home":
      default:
        return (
          <HomeScreen
            torneo={torneo}
            isSaved={isSaved}
            onInfoChange={onInfoChange}
            onRuedasChange={onRuedasChange}
            onOpenTeams={() => push({ level: "teams" })}
            onOpenRound={(roundId) => push({ level: "round", roundId })}
            onOpenEstructura={(zonas) => push({ level: "estructura", zonas })}
            onCreate={handleCreate}
          />
        );
    }
  };

  return (
    <>
      <PanelHeader size="sm" />
      <div className="content">
        <Row>
          <Col xs={12}>
            <Card>
              <CardBody>
                {loading ? (
                  <p className="text-muted text-center py-5 my-0">Cargando torneo…</p>
                ) : (
                  renderScreen()
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
      {isSaved && saveState !== "idle" && (
        <div className={"save-indicator save-indicator--" + saveState}>
          {saveState === "saving" && "Guardando…"}
          {saveState === "saved" && "Guardado"}
          {saveState === "error" && "Error al guardar"}
        </div>
      )}
    </>
  );
}
